import { useCallback, useEffect, useState } from 'react'
import { useActiveWeb3React } from '../../hooks'
import useDebounce from '../../hooks/useDebounce'
import useIsWindowVisible from '../../hooks/useIsWindowVisible'
import { updateBlockNumber } from './actions'
import { useDispatch } from 'react-redux'

// 检查是否是网络变化错误
const isNetworkChangeError = (error: any): boolean => {
  return (
    error &&
    ((error.message &&
      (error.message.includes('underlying network changed') ||
        error.message.includes('NETWORK_ERROR') ||
        error.message.includes('network changed'))) ||
      error.code === 'NETWORK_ERROR' ||
      error.code === -32603)
  )
}

export default function Updater(): null {
  const { library, chainId } = useActiveWeb3React()
  const dispatch = useDispatch()

  const windowVisible = useIsWindowVisible()

  const [state, setState] = useState<{ chainId: number | undefined; blockNumber: number | null }>({
    chainId,
    blockNumber: null
  })

  const blockNumberCallback = useCallback(
    (blockNumber: number) => {
      try {
        setState(state => {
          if (chainId === state.chainId) {
            if (typeof state.blockNumber !== 'number') return { chainId, blockNumber }
            return { chainId, blockNumber: Math.max(blockNumber, state.blockNumber) }
          }
          return state
        })
      } catch (error) {
        // 检查是否是网络变化错误
        if (isNetworkChangeError(error)) {
          console.debug('Network change error in blockNumberCallback, ignoring:', error)
          return
        }
        console.error('Error in blockNumberCallback:', error)
      }
    },
    [chainId, setState]
  )

  // attach/detach listeners
  useEffect(() => {
    if (!library || !chainId || !windowVisible) return undefined

    setState({ chainId, blockNumber: null })

    library
      .getBlockNumber()
      .then(blockNumberCallback)
      .catch(error => {
        // 检查是否是网络变化错误
        if (isNetworkChangeError(error)) {
          console.debug('Network change error in getBlockNumber, ignoring:', error)
          return
        }
        console.error(`Failed to get block number for chainId: ${chainId}`, error)
      })

    library.on('block', blockNumberCallback)
    return () => {
      library.removeListener('block', blockNumberCallback)
    }
  }, [dispatch, chainId, library, blockNumberCallback, windowVisible])

  const debouncedState = useDebounce(state, 100)

  useEffect(() => {
    if (!debouncedState.chainId || !debouncedState.blockNumber || !windowVisible) return
    dispatch(updateBlockNumber({ chainId: debouncedState.chainId, blockNumber: debouncedState.blockNumber }))
  }, [windowVisible, dispatch, debouncedState.blockNumber, debouncedState.chainId])

  return null
}
